<template>
    <!-- Presentational keyboard, mirrors DartScoreGrid: a turn is up to
         `dartsPerTurn` darts that you can undo/confirm. The middle columns come
         from `scoreOptions`: Race mode passes a single target segment, Points mode
         passes the ways to score the target number (single/double/triple, or the
         two bulls). All turn state lives in the parent — this component just
         renders it and emits intents. -->
    <div
        class="bg-bg relative flex flex-col"
        :style="{ paddingBottom: 'calc(1rem + var(--safezone-bottom, 0px))' }"
    >
        <!-- Dim the grid while input is locked (submitting / no player / match over). -->
        <div v-if="disabled" class="absolute inset-0 z-20 bg-bg/60"></div>

        <div class="flex flex-col">
            <!-- Entered darts + confirm -->
            <div class="relative flex items-center gap-4 p-4">
                <div class="relative flex-1">
                    <div class="flex justify-evenly gap-4">
                        <div
                            v-for="index in dartsPerTurn"
                            :key="index"
                            class="bg-card border-stroke-1 flex h-15 flex-1 items-center justify-center border transition-all"
                            :class="{
                                'bg-card-2 border-red-500':
                                    index - 1 === 0 && enteredDarts.length === 0,
                                'border-green-500': index - 1 === enteredDarts.length,
                            }"
                        >
                            <span class="font-title font-semibold text-white">{{
                                formatDartDisplay(enteredDarts[index - 1]) || ''
                            }}</span>
                        </div>
                    </div>
                </div>

                <button
                    @click="$emit('submit')"
                    :disabled="disabled || (scoreOptions.length !== 0 && enteredDarts.length < dartsRemaining) || isSubmitting"
                    data-testid="segments-submit"
                    class="clip-left-corner relative h-15 bg-red-600 px-4 text-base font-semibold tracking-tight text-white uppercase transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                    <span
                        v-if="isSubmitting"
                        class="absolute inset-0 flex items-center justify-center bg-red-600"
                    >
                        <svg
                            class="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                    </span>
                    <span :class="{ 'opacity-0': isSubmitting }">{{ $t('segments_game.submit') }}</span>
                </button>
            </div>

            <!-- UNDO | score option(s) | MISS.
                 Flex-wrap with a one-third basis: exactly 3 buttons fit per row,
                 and a partial last row grows (flex-1) to fill the full width
                 (e.g. 5 buttons -> 3 at 1/3, then 2 at 1/2). The 1px gap over the
                 stroke-colored container renders the dividers. -->
            <div class="bg-stroke-1 border-stroke-1 flex flex-wrap gap-px border-t border-b">
                <button
                    v-for="option in scoreOptions"
                    :key="option.label"
                    :disabled="inputLocked || dartLimitReached"
                    data-testid="segments-score-option"
                    @click="$emit('hit', option)"
                    class="bg-bg hover:bg-card flex-1 basis-[calc((100%-2px)/3)] py-5 text-lg font-bold text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {{ option.label }}
                </button>
                <button
                    :disabled="inputLocked || enteredDarts.length === 0"
                    data-testid="segments-undo"
                    @click="$emit('undo')"
                    class="bg-bg hover:bg-card flex-1 basis-[calc((100%-2px)/3)] py-5 text-lg font-bold text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {{ $t('segments_game.undo') }}
                </button>
                <button
                    :disabled="inputLocked || dartLimitReached"
                    data-testid="segments-miss"
                    @click="$emit('miss')"
                    class="bg-bg hover:bg-card flex-1 basis-[calc((100%-2px)/3)] py-5 text-lg font-bold text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {{ $t('segments_game.miss') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { DART_TYPES } from '@/constants/dartTypes.js'

export default {
    name: 'SegmentsKeyboard',
    props: {
        // The darts entered so far this turn: [{ dart_type, dart_number }].
        enteredDarts: {
            type: Array,
            default: () => [],
        },
        // The ways to score the current target, each rendered as a button:
        // [{ label, dart_type, dart_number }]. Emitted back via `hit`.
        scoreOptions: {
            type: Array,
            default: () => [],
        },
        dartsPerTurn: {
            type: Number,
            default: 3,
        },
        // How many darts still fit in the active player's current turn. Submit is
        // gated on filling all of these, so a full turn requires dartsPerTurn darts
        // but a turn that already holds submitted darts requires fewer. This stops a
        // submission from overflowing the turn and spilling darts into the next one.
        dartsRemaining: {
            type: Number,
            default: 3,
        },
        isSubmitting: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['hit', 'miss', 'undo', 'submit'],
    data() {
        return {
            DART_TYPES,
        }
    },
    computed: {
        dartLimitReached() {
            return this.enteredDarts.length >= this.dartsRemaining
        },
        // Input is locked while disabled (no player / match over) or mid-submit.
        inputLocked() {
            return this.disabled || this.isSubmitting
        },
    },
    methods: {
        formatDartDisplay(dart) {
            if (!dart) return ''
            if (dart.dart_type === DART_TYPES.BULL) return '50'
            if (dart.dart_type === DART_TYPES.OUTER_BULL) return '25'
            if (dart.dart_type === DART_TYPES.MISS) return '-'
            return dart.dart_type === DART_TYPES.SINGLE
                ? dart.dart_number.toString()
                : `${dart.dart_type}${dart.dart_number}`
        },
    },
}
</script>
